import { Cross2Icon, Pencil1Icon } from "@radix-ui/react-icons";
import { useTypeWire } from "@typewirets/react";
import { Calculator, Plus } from "lucide-react";
import { useCallback, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  FieldDefinition,
  MetricDefinition,
} from "@/hooks/api/use-field-definitions";
import { FormulaValidationServiceWire } from "@/lib/formula-validation-service.wire";
import { cn } from "@/lib/utils";
import type { FormulaColumnDefinition } from "@/types/formula-column";
import { FormulaColumnBuilder } from "./formula-column-builder";

interface FormulaColumnManagerProps {
  formulaColumns: FormulaColumnDefinition[];
  visibleColumnIds: string[];
  onCreateColumn: (
    column: Omit<FormulaColumnDefinition, "id" | "createdAt" | "updatedAt">,
  ) => void;
  onUpdateColumn: (
    id: string,
    updates: Partial<Omit<FormulaColumnDefinition, "id" | "createdAt">>,
  ) => void;
  onDeleteColumn: (id: string) => void;
  onToggleColumnVisibility: (columnId: string) => void;
  className?: string;
  fieldDefs?: FieldDefinition[];
  availableMetrics?: MetricDefinition[];
}

/**
 * Component for managing formula columns with AST-based validation.
 *
 * Provides a dropdown interface for creating, editing, and managing formula columns.
 * Uses the FormulaParser for syntax validation without client-side evaluation.
 */
export function FormulaColumnManager({
  formulaColumns,
  visibleColumnIds,
  onCreateColumn,
  onUpdateColumn,
  onDeleteColumn,
  onToggleColumnVisibility,
  className,
  fieldDefs,
  availableMetrics,
}: FormulaColumnManagerProps) {
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState<
    FormulaColumnDefinition | undefined
  >();
  const [deleteColumn, setDeleteColumn] = useState<
    FormulaColumnDefinition | undefined
  >();

  // Get validation service from TypeWire
  const validationService = useTypeWire(FormulaValidationServiceWire);

  const handleCreateColumn = useCallback(() => {
    setEditingColumn(undefined);
    setBuilderOpen(true);
  }, []);

  const handleEditColumn = useCallback((column: FormulaColumnDefinition) => {
    setEditingColumn(column);
    setBuilderOpen(true);
  }, []);

  const handleSaveColumn = useCallback(
    (
      columnData: Omit<
        FormulaColumnDefinition,
        "id" | "createdAt" | "updatedAt"
      >,
    ) => {
      if (editingColumn) {
        onUpdateColumn(editingColumn.id, columnData);
      } else {
        onCreateColumn(columnData);
      }
      setEditingColumn(undefined);
    },
    [editingColumn, onCreateColumn, onUpdateColumn],
  );

  const handleDeleteColumn = useCallback((column: FormulaColumnDefinition) => {
    setDeleteColumn(column);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteColumn) {
      onDeleteColumn(deleteColumn.id);
      setDeleteColumn(undefined);
    }
  }, [deleteColumn, onDeleteColumn]);

  const visibleFormulaColumns = formulaColumns.filter((col) =>
    visibleColumnIds.includes(`formula_${col.id}`),
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn("flex items-center gap-1.5", className)}
          >
            <Calculator
              className={cn(
                "h-4 w-4",
                visibleFormulaColumns.length > 0
                  ? "text-primary"
                  : "text-muted-foreground",
              )}
            />
            <span>Formula Columns</span>
            {visibleFormulaColumns.length > 0 && (
              <Badge className="ml-1 bg-primary text-primary-foreground">
                {visibleFormulaColumns.length}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-80" align="end">
          <DropdownMenuLabel className="flex items-center justify-between">
            Formula Columns
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCreateColumn}
              className="h-6 px-2"
            >
              <Plus className="h-3 w-3 mr-1" />
              New
            </Button>
          </DropdownMenuLabel>

          {formulaColumns.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <div className="max-h-60 overflow-y-auto">
                {formulaColumns.map((column) => {
                  const isVisible = visibleColumnIds.includes(
                    `formula_${column.id}`,
                  );

                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={isVisible}
                      onCheckedChange={() =>
                        onToggleColumnVisibility(`formula_${column.id}`)
                      }
                      className="py-2.5"
                      onSelect={(e) => {
                        e.preventDefault();
                      }}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {column.name}
                          </div>
                          {column.description && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <p className="text-xs text-muted-foreground truncate mt-1">
                                  {column.description}
                                </p>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-xs">
                                <p>{column.description}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>

                        <div className="flex">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleEditColumn(column);
                            }}
                          >
                            <Pencil1Icon className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive/90"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleDeleteColumn(column);
                            }}
                          >
                            <Cross2Icon className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </div>
            </>
          )}

          {formulaColumns.length === 0 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              <Calculator className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No formula columns yet</p>
              <p className="text-xs">
                Create calculated columns using Excel-like formulas
              </p>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <FormulaColumnBuilder
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        onSave={handleSaveColumn}
        onDelete={onDeleteColumn}
        validationService={validationService}
        existingColumn={editingColumn}
        fieldDefs={fieldDefs}
        availableMetrics={availableMetrics}
      />

      <AlertDialog
        open={!!deleteColumn}
        onOpenChange={(open) => !open && setDeleteColumn(undefined)}
      >
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Formula Column</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the formula column "
              {deleteColumn?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
